import { useMemo } from 'react';
import { useApprovalsData } from 'content-management-data-lib';
import { createFiltersConfiguration, createPropertyDefinitionMapForProperties } from '../utils/createContentPlatformListingPropertiesConfigurationUtils';
import { getApprovalStatusPropertyOptions, getAudienceAccessPropertyOptions, getContentPublishStatusPropertyOptions } from '../utils/filterPropertyUtils';
import { CONTENT_PLATFORM_PROPERTY_NAME } from 'content-management-data-lib';
import useContentGroups from '../hooks/useContentGroups';
import ContentPlatformPropertiesConfigurationContext from './ContentPlatformPropertiesConfigurationContext';
import useLanguages from '../hooks/useLanguages';
import { USER, CAMPAIGN, TEAM
// @ts-expect-error Untyped module
} from 'reference-resolvers/constants/ReferenceObjectTypes';
import { jsx as _jsx } from "react/jsx-runtime";
const useStandardPropertiesVisibilityAndOptionsMap = contentGroupPropertyOptions => {
  const {
    languagePropertyOptions
  } = useLanguages();
  const {
    areApprovalsEnabled
  } = useApprovalsData();
  return useMemo(() => {
    return {
      [CONTENT_PLATFORM_PROPERTY_NAME.APPROVAL_PUBLISH_STATUS]: {
        hidden: !areApprovalsEnabled,
        options: getApprovalStatusPropertyOptions()
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.LANGUAGE]: {
        options: languagePropertyOptions
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.GROUP_ID]: {
        options: contentGroupPropertyOptions
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.AUDIENCE_ACCESS]: {
        options: getAudienceAccessPropertyOptions()
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.STATE]: {
        options: getContentPublishStatusPropertyOptions()
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_BY_USER_ID]: {
        referencedObjectType: USER,
        externalOptionsReferenceType: USER,
        externalOptions: true
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.CREATED_BY_USER_ID]: {
        referencedObjectType: USER,
        externalOptionsReferenceType: USER,
        externalOptions: true,
        type: 'enumeration',
        fieldType: 'select'
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.CAMPAIGN]: {
        referencedObjectType: CAMPAIGN,
        externalOptionsReferenceType: CAMPAIGN,
        externalOptions: true
      },
      [CONTENT_PLATFORM_PROPERTY_NAME.TEAM_PERMS]: {
        referencedObjectType: TEAM,
        externalOptionsReferenceType: TEAM,
        externalOptions: true
      }
    };
  }, [areApprovalsEnabled, contentGroupPropertyOptions, languagePropertyOptions]);
};
const ContentPlatformPropertiesConfigurationProvider = ({
  filterValidationConfig,
  contentGroupObjectType,
  propertyOverrides,
  propertyFilterConfigs,
  children
}) => {
  const {
    contentGroupPropertyOptions,
    contentGroupsLoaded
  } = useContentGroups(contentGroupObjectType);
  const standardPropertiesVisibilityAndOptionsMap = useStandardPropertiesVisibilityAndOptionsMap(contentGroupPropertyOptions);
  const propertyDefinitionMap = useMemo(() => {
    // Merge ContentPlatform standard properties into the configuration
    return createPropertyDefinitionMapForProperties({
      standardPropertiesVisibilityAndOptionsMap,
      propertyOverrides
    });
  }, [propertyOverrides, standardPropertiesVisibilityAndOptionsMap]);
  const {
    quickFilterPropertyNames,
    filterTagsConfig
  } = useMemo(() => {
    return createFiltersConfiguration({
      propertyFilterConfigs
    });
  }, [propertyFilterConfigs]);
  const listingPropertiesConfiguration = useMemo(() => {
    return {
      propertyDefinitionMap,
      filterValidationConfig,
      quickFilterPropertyNames,
      filterTagsConfig
    };
  }, [filterTagsConfig, filterValidationConfig, propertyDefinitionMap, quickFilterPropertyNames]);

  // If contentGroupObjectType is provided, but contentGroups are not loaded yet, fetcher may not be ready
  // as it will often depend on content group data for multi-lang group association and/or emptyStateFilters
  if (contentGroupObjectType && !contentGroupsLoaded) {
    return null;
  }
  return /*#__PURE__*/_jsx(ContentPlatformPropertiesConfigurationContext.Provider, {
    value: listingPropertiesConfiguration,
    children: children
  });
};
export default ContentPlatformPropertiesConfigurationProvider;